import React from "react"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { Icon } from "~/react/ui/icon"
import { TooltipProvider } from "~/react/ui/tooltip"
import {
  Filters,
  clearFilters,
  filterFunction,
  sortFunction,
  type FiltersType,
} from "~/react/components/search/filters"
import { CostCalculator } from "~/helpers/costCalculator"
import { type TabSelected } from "~/react/types/tabSelected"
import {
  leads_path,
  multi_domain_search_emails_search_index_path,
} from "~/lib/routes"
import { useAsyncQueuer } from "@tanstack/react-pacer"
import BulkActionsToolbar from "~/react/components/search/bulk-actions-toolbar"
import { useCampaignsSave } from "~/react/hooks/useCampaignsSave"
import { useLeadsListSave } from "~/react/hooks/useLeadsListSave"
import { useJobTitleSuggestions } from "~/react/hooks/useJobTitleSuggestions"
import { useSplittedResults } from "~/react/hooks/useSplittedResults"
import EmailsList from "~/react/components/search/emails-list"
import { useLocationMappings } from "~/react/hooks/useLocationMappings"
import { Snackbar, SnackbarText, SnackbarLink } from "~/react/ui/snackbar"
import { useEmailSearch } from "~/react/hooks/useEmailSearch"
import { useExportEmails } from "~/react/hooks/useExportEmails"

interface DomainSearchProps {
  emailIds: number[]
  numberOfResults: number
  genericEmailsCount: number
  personalEmailsCount: number
  decisionMakerTotalCount: number
  departmentTotalCounts: Record<string, number>
  filterParams: Record<string, any>
  domain: {
    value: string
    company_name?: string
    company_data: boolean
  }
  leadsLists: { id: number; name: string }[]
  campaigns: { id: number; name: string }[]
  numberOfCreditsLeft: number
  singleCreditsBucket: boolean
  jobTitleProposals: string[]
  revealEmailPattern: boolean
  linkedDomains: string[]
  companySignal?: any
  clickToCopy?: boolean
}

type LocationsMappingsType = {
  continents: string[]
  business_regions: string[]
}

export default function DomainSearch({
  emailIds: initialEmailIds,
  numberOfResults,
  genericEmailsCount,
  personalEmailsCount,
  decisionMakerTotalCount,
  departmentTotalCounts,
  filterParams,
  domain,
  numberOfCreditsLeft,
  singleCreditsBucket,
  leadsLists: initialLeadsLists,
  campaigns: initialCampaigns,
  jobTitleProposals,
  revealEmailPattern,
  linkedDomains,
  companySignal,
  clickToCopy = false,
}: DomainSearchProps) {
  const [isLoading, setIsLoading] = React.useState(false)
  const [leadsLists, setLeadsLists] =
    React.useState<{ id: number; name: string }[]>(initialLeadsLists)
  const [campaigns, setCampaigns] =
    React.useState<{ id: number; name: string }[]>(initialCampaigns)
  const [tabSelected, setTabSelected] =
    React.useState<TabSelected>("all_people")
  const locationsMappings = useLocationMappings()
  const [filters, setFilters] = React.useState<FiltersType>({
    requiredFields: [],
    departments: [],
    jobTitles: [],
    verificationStatus: [],
    hideAlreadySaved: false,
    location: [],
  })

  const {
    results,
    setResults,
    revealingIds,
    revealedIds,
    selectedIds,
    revealedLeadsListId,
    operationId,
    lastRevealedIds,
    handleReveal,
    handleRevealMultiple,
    handleSelect,
    setSelectedIds,
  } = useEmailSearch({
    source: "search",
    context: "search",
  })

  const revealingGeneric = React.useMemo(() => {
    return revealingIds.some(
      (id) => results.find((result) => result.id === id)?.type === "generic",
    )
  }, [revealingIds, results])

  // Loading hook for fetching emails
  const fetchEmailsFn = async (emailIds: number[]) => {
    const response = await fetch(
      multi_domain_search_emails_search_index_path(),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
            ?.content,
        },
        body: JSON.stringify({ email_ids: emailIds }),
      },
    )
    const data = await response.json()

    setResults((prev) => {
      const existingIds = new Set(prev.map((result) => result.id))
      const newResults = data.emails.filter(
        (email) => !existingIds.has(email.id),
      )
      return [...prev, ...newResults]
    })
    if (queue.peekPendingItems().length === 0) {
      setIsLoading(false)
    }
  }
  const queue = useAsyncQueuer(fetchEmailsFn, { concurrency: 10, wait: 10 })

  // Load initial emails
  React.useEffect(() => {
    if (initialEmailIds.length > 0) {
      setIsLoading(true)
      const batchSize = 5000

      for (let i = 0; i < initialEmailIds.length; i += batchSize) {
        queue.addItem(initialEmailIds.slice(i, i + batchSize))
      }
    }
  }, [initialEmailIds])

  const handleCampaignsSave = useCampaignsSave(results, campaigns, setCampaigns)
  const handleLeadsListSave = useLeadsListSave(
    results,
    setLeadsLists,
    setResults,
  )

  // Filter and sort results
  const filteredResults = React.useMemo(() => {
    const baseFilteredResults = filterFunction(
      results,
      filters,
      locationsMappings,
      revealedIds,
    )

    return baseFilteredResults.sort(sortFunction)
  }, [results, filters, revealedIds])

  const { allPeopleResults, decisionMakersResults, genericResults } =
    useSplittedResults(filteredResults)

  const jobTitles = useJobTitleSuggestions(
    results,
    filters,
    locationsMappings,
    revealedIds,
  )

  const anyFiltersSet =
    filters.departments.length > 0 ||
    filters.jobTitles.length > 0 ||
    filters.requiredFields.length > 0 ||
    filters.hideAlreadySaved ||
    filters.verificationStatus.length > 0 ||
    filters.location.length > 0

  // Bulk actions functions
  const saveLeads = async (listId: number | null) => {
    if (selectedIds.length > 0) {
      // Reveal only specific emails
      handleRevealMultiple(selectedIds, { leads_list_id: listId ?? undefined })
    }
  }

  const exportEmails = useExportEmails(domain.value)

  const handleLeadManagerSave = async (name: string, listId: number) => {
    const listAlreadyExists = leadsLists.find((list) => list.id === listId)

    if (!listAlreadyExists) {
      setLeadsLists((prev) => [...prev, { id: listId, name }])
    }

    saveLeads(listId)
  }

  // Calculate costs
  let numberOfEmailsToReveal = 0
  if (selectedIds.length > 0) {
    numberOfEmailsToReveal = results.filter(
      (result) => selectedIds.includes(result.id) && !result.revealed,
    ).length
  }

  const numberOfCreditsNeeded = CostCalculator.calculate(
    "search",
    numberOfEmailsToReveal,
  )
  let neededCredits = numberOfCreditsNeeded - numberOfCreditsLeft
  if (neededCredits < 0) {
    neededCredits = 0
  }
  const revealAllOperationCost = CostCalculator.calculateVisualRepresentation(
    "search",
    numberOfEmailsToReveal,
  )

  const genericRevealed = React.useMemo(() => {
    return revealingGeneric || genericResults.some((result) => result.revealed)
  }, [genericResults, revealingGeneric])

  const alreadyRevealedCount = selectedIds.filter(
    (id) => results.find((result) => result.id === id)?.revealed,
  ).length

  const snackbarLinkParams: Record<string, number | null> = {}
  if (operationId) {
    snackbarLinkParams.operation_id = operationId
  }
  if (revealedLeadsListId) {
    snackbarLinkParams.leads_list_id = revealedLeadsListId
  }
  if (lastRevealedIds.length === 1) {
    const result = results.find((result) => lastRevealedIds.includes(result.id))

    if (result) {
      snackbarLinkParams.email = result.email
    }
  }

  return (
    <>
      <TooltipProvider>
        <div className="board-box ds-results">
          <header className="ds-results__header">
            <h2
              className="ds-results__title"
              data-domain-search--search-target="resultsTitle"
            >
              <strong>
                {i18n.t("js.search.results", { count: numberOfResults })}
              </strong>{" "}
              {i18n.t("js.search.for")} {domain.value}
            </h2>
            <form
              className="ds-finder-form ds-finder-form--makers"
              data-controller="domain-search--email-finder loading"
              data-domain-search--email-finder-domain-value={domain.value}
              data-loading-text-value=""
              data-domain-search--email-finder-version-value="domain_search"
              data-action="domain-search--email-finder#find loading#show domain-search--email-finder:loaded@window->loading#hide domain-search--email-finder:loaded@window->domain-search--search#updateResultsTitle"
            >
              <div className="ds-finder-form-name">
                <svg className="ds-finder-form-name__icon">
                  <use xlinkHref="#icon-email-finder" />
                </svg>
                <input
                  id="full-name-field"
                  className="ds-finder-form-name__field"
                  type="text"
                  placeholder={i18n.t(
                    "js.search.find_by_name.fullname_placeholder",
                  )}
                  data-domain-search--email-finder-target="name"
                  data-focus-element-target="element"
                />
              </div>
              <button
                className="ds-finder-form__submit"
                type="button"
                aria-label={i18n.t("js.search.find_by_name.find_this_person")}
                data-loading-target="element"
                data-action="domain-search--email-finder#find loading#show"
              >
                <span className="far fa-search"></span>
              </button>
            </form>
          </header>

          <Filters
            id="company-emails-filters"
            filters={filters}
            setFilters={(newFilters) => setFilters(newFilters)}
            jobTitles={jobTitles}
            anyFiltersSet={anyFiltersSet}
            clearFilters={() => clearFilters(setFilters)}
            possibleFilters={[
              "department",
              "job_title",
              "already_saved",
              "verification_status",
              "required_field",
              "location",
            ]}
          />

          <turbo-frame id="domain_search_email_finder_email" />

          {/* Bulk Actions Toolbar - matches original ds-bulk-actions structure */}
          <BulkActionsToolbar
            className={selectedIds.length > 0 ? "is-visible" : ""}
            selectedIds={selectedIds}
            numberOfResults={numberOfResults}
            currentCredits={numberOfCreditsLeft}
            alreadyRevealedCount={alreadyRevealedCount}
            currentTab={tabSelected}
            numberOfResultsForCurrentTab={
              tabSelected === "all_people"
                ? allPeopleResults.length
                : tabSelected === "decision_makers"
                  ? decisionMakersResults.length
                  : genericResults.length
            }
            removeSelection={() => setSelectedIds([])}
            selectAll={() =>
              setSelectedIds(filteredResults.map((result) => result.id))
            }
            selectCurrentTab={() => {
              const resultsToSelect =
                tabSelected === "all_people"
                  ? allPeopleResults
                  : tabSelected === "decision_makers"
                    ? decisionMakersResults
                    : genericResults

              setSelectedIds((prev) => [
                ...new Set([
                  ...prev,
                  ...resultsToSelect.map((result) => result.id),
                ]),
              ])
            }}
            saveLeads={saveLeads}
            handleLeadManagerSave={handleLeadManagerSave}
            leadsLists={leadsLists}
            revealingIds={revealingIds}
            revealAllOperationCost={revealAllOperationCost}
            exportEmails={() => exportEmails(selectedIds)}
          />

          <EmailsList
            results={filteredResults}
            anyFiltersSet={anyFiltersSet}
            selectedIds={selectedIds}
            campaigns={campaigns}
            leadsLists={leadsLists}
            isLoading={isLoading}
            showGenericAlert={!genericRevealed}
            handleReveal={handleReveal}
            handleRevealMultiple={handleRevealMultiple}
            handleLeadsListSave={handleLeadsListSave}
            handleCampaignsSave={handleCampaignsSave}
            handleSelect={handleSelect}
            onTabSelected={setTabSelected}
            clearFilters={() => clearFilters(setFilters)}
          />

          {lastRevealedIds.length > 0 && (
            <Snackbar className="m-4">
              <Icon
                icon="check-circle"
                variant="solid"
                size="sm"
                className="text-success"
              />
              <SnackbarText>
                <strong className="fw-medium">
                  {i18n.t("js.multi_domain_search.footer.leads", {
                    count: lastRevealedIds.length,
                    pretty_count: CurrencyHelper.numberWithDelimiter(
                      lastRevealedIds.length,
                    ),
                  })}
                </strong>
                &nbsp;
                {i18n.t("js.multi_domain_search.footer.saved")}
              </SnackbarText>
              <SnackbarLink
                href={leads_path(snackbarLinkParams)}
                turboFrame="_top"
              >
                {i18n.t("js.multi_domain_search.footer.see_in_leads")}
              </SnackbarLink>
            </Snackbar>
          )}
        </div>
      </TooltipProvider>
    </>
  )
}
